"use client";

import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import FieldError from "@/components/ui/FieldError";
import { CategoryDropdown } from "@/components/CategoryDropdown";
import RequestItemPreferenceFields, {
  requestItemHasPreferenceFields,
} from "@/components/item-requests/RequestItemPreferenceFields";
import SellLocationField from "@/components/listing/SellLocationField";
import {
  useItemRequestCapture,
  type ItemRequestCaptureSubmitValues,
} from "@/lib/hooks/useItemRequestCapture";
import { useActiveCategoryStructure } from "@/lib/hooks/useActiveCategoryStructure";
import { sellProfileLocationFromUser } from "@/lib/listing/listingLocationIds";
import { ITEM_REQUEST_CAPTURE_COPY } from "@/lib/item-requests";
import { formatPrice } from "@/lib/utils";
import type { User } from "@/lib/types";

export type RequestItemCaptureFormValues = ItemRequestCaptureSubmitValues;

type Props = {
  /** Logged-in user — used for profile location shortcut (same as sell). */
  user?: User | null;
  isLoading: boolean;
  onSubmit: (values: RequestItemCaptureFormValues) => Promise<void>;
  onCancel?: () => void;
};

export default function RequestItemCaptureForm({
  user,
  isLoading,
  onSubmit,
  onCancel,
}: Props) {
  const profileLocation = sellProfileLocationFromUser(user);
  const capture = useItemRequestCapture({ profileLocation });
  const { activeCategoryStructure } = useActiveCategoryStructure();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Enter on mid-steps advances only — never create (H5 / Phase 2).
    if (capture.step !== "finish") {
      capture.goNext();
      return;
    }
    const built = capture.tryBuildSubmitValues();
    if (!built.ok) return;
    await onSubmit(built.values);
  };

  const showPrefs =
    capture.selection &&
    requestItemHasPreferenceFields(
      capture.selection.categoryId,
      capture.selection.subcategoryId,
    );

  const continueDisabled =
    capture.step === "what"
      ? !capture.canLeaveWhat
      : capture.step === "budget"
        ? !capture.canLeaveBudget
        : false;

  const primaryDisabled =
    capture.step === "finish"
      ? !capture.canSubmit || isLoading
      : continueDisabled;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-text-light mb-1">
          Step {capture.stepMeta.index} of {capture.totalSteps}
        </p>
        <h2 className="text-xl font-semibold text-foreground mb-1">
          {capture.stepMeta.title}
        </h2>
        {capture.step === "what" ? (
          <p className="text-text-light text-sm">
            Select the details of what you want.
          </p>
        ) : null}
        {capture.step === "budget" && capture.lookingForLine ? (
          <p className="text-text-light text-sm">
            Looking for:{" "}
            <span className="font-medium text-foreground">
              {capture.lookingForLine}
            </span>
          </p>
        ) : null}
        {capture.step === "finish" && capture.lookingForLine ? (
          <p className="text-text-light text-sm">
            Looking for:{" "}
            <span className="font-medium text-foreground">
              {capture.lookingForLine}
            </span>
            {capture.maxPrice ? (
              <>
                {" "}
                · Max{" "}
                <span className="font-medium text-foreground">
                  ₦{capture.maxPrice}
                </span>
              </>
            ) : null}
          </p>
        ) : null}
      </div>

      {capture.step === "what" ? (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Category *
            </label>
            <CategoryDropdown
              onSelect={capture.setSelection}
              placeholder="Select category and subcategory"
              categoryStructure={activeCategoryStructure}
              initialValue={capture.selection ?? undefined}
            />
            {capture.composedTitle ? (
              <p className="mt-2 text-sm text-text-light">
                Looking for:{" "}
                <span className="font-medium text-foreground">
                  {capture.composedTitle}
                </span>
              </p>
            ) : null}
          </div>

          {showPrefs && capture.selection ? (
            <RequestItemPreferenceFields
              categoryId={capture.selection.categoryId}
              subcategoryId={capture.selection.subcategoryId}
              categoryStructure={activeCategoryStructure}
              brand={capture.brand}
              size={capture.size}
              model={capture.model}
              familySpecs={capture.familySpecs}
              onBrandChange={capture.setBrand}
              onSizeChange={capture.setSize}
              onModelChange={capture.setModel}
              onFamilySpecsChange={capture.setFamilySpecs}
            />
          ) : null}
        </div>
      ) : null}

      {capture.step === "budget" ? (
        <div className="space-y-6">
          {capture.insightLoading ? (
            <p className="text-sm text-text-light" role="status">
              {ITEM_REQUEST_CAPTURE_COPY.insightLoading}
            </p>
          ) : null}

          {capture.showInsightBlock && capture.insightData ? (
            <div className="rounded-lg border border-border p-4 space-y-2">
              <h4 className="text-sm font-medium text-foreground">
                What people usually pay
              </h4>
              <p className="text-sm text-text-light">
                Typical used range{" "}
                <span className="font-medium text-foreground">
                  {formatPrice(capture.insightData.priceRange.minPrice)} –{" "}
                  {formatPrice(capture.insightData.priceRange.maxPrice)}
                </span>
              </p>
              <p className="text-sm text-text-light">
                Suggested budget{" "}
                <span className="font-medium text-foreground">
                  ~ {formatPrice(capture.insightData.suggestedPrice)}
                </span>
              </p>
              <Button
                type="button"
                variant="secondary"
                className="mt-1"
                onClick={capture.applySuggested}
              >
                Use suggested
              </Button>
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Your max budget (₦){" "}
              <span className="text-text-light font-normal">(optional)</span>
            </label>
            <Input
              type="text"
              name="maxPrice"
              value={capture.maxPrice}
              onChange={capture.handleMaxPriceChange}
              placeholder="e.g., 220,000"
              className="w-full"
            />
          </div>

          {capture.guidance.showSoftWarn ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
              <p className="text-sm text-amber-950">
                {capture.guidance.message}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={capture.applySuggested}>
                  Raise to ~
                  {formatPrice(
                    capture.guidance.suggestedMax ??
                      capture.insightData?.suggestedPrice ??
                      0,
                  )}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={capture.acknowledgeSoftWarn}
                >
                  Keep anyway
                </Button>
              </div>
              {capture.needsAckHint && !capture.budgetWarnAck ? (
                <p className="text-xs text-amber-900 font-medium">
                  Raise your budget or choose Keep anyway before continuing.
                </p>
              ) : null}
              {capture.budgetWarnAck ? (
                <p className="text-xs text-amber-900">
                  {ITEM_REQUEST_CAPTURE_COPY.softWarnAckContinue}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {capture.step === "finish" ? (
        <div className="space-y-6">
          <p className="text-sm text-text-light">
            {ITEM_REQUEST_CAPTURE_COPY.finishSaveHint}
          </p>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              How soon *
            </label>
            <select
              name="urgency"
              value={capture.urgency}
              onChange={(e) =>
                capture.setUrgency(e.target.value as "low" | "medium" | "high")
              }
              className="w-full p-3 border border-border rounded-lg bg-surface text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="low">No rush</option>
              <option value="medium">Soon</option>
              <option value="high">Urgent</option>
            </select>
          </div>

          <div>
            <SellLocationField
              label="Where should we look?"
              value={capture.location}
              onChange={capture.handleLocationChange}
              profileLocation={capture.profileLocation}
              placeholder="Select area for matching"
            />
            <p className="mt-2 text-xs text-text-light">
              Defaults to your profile when set — change it if you want matches
              in another area.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {ITEM_REQUEST_CAPTURE_COPY.notesLabel}{" "}
              <span className="text-text-light font-normal">
                {ITEM_REQUEST_CAPTURE_COPY.notesOptionalHint}, max{" "}
                {capture.notesMaxLen} chars
              </span>
            </label>
            <textarea
              name="notes"
              value={capture.notes}
              onChange={(e) => capture.setNotesCapped(e.target.value)}
              onBlur={capture.validateNotesOnBlur}
              rows={3}
              placeholder={ITEM_REQUEST_CAPTURE_COPY.notesPlaceholder}
              aria-invalid={Boolean(capture.notesError)}
              aria-describedby={
                capture.notesError ? "item-request-notes-error" : undefined
              }
              className={`w-full p-3 border rounded-lg bg-surface text-foreground focus:ring-2 focus:ring-primary focus:border-primary ${
                capture.notesError ? "border-red-500" : "border-border"
              }`}
              maxLength={capture.notesMaxLen}
            />
            <p className="mt-1 text-xs text-text-light">
              {ITEM_REQUEST_CAPTURE_COPY.notesHelper}
            </p>
            <FieldError
              id="item-request-notes-error"
              message={capture.notesError ?? undefined}
            />
          </div>
        </div>
      ) : null}

      {capture.stepHint ? (
        <p className="text-sm text-amber-900 font-medium">{capture.stepHint}</p>
      ) : null}

      <div className="flex flex-wrap justify-between gap-3">
        <div className="flex gap-3">
          {capture.step !== "what" ? (
            <Button type="button" variant="secondary" onClick={capture.goBack}>
              ← Back
            </Button>
          ) : onCancel ? (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1 ml-auto">
          {capture.step === "finish" ? (
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={primaryDisabled}
              className="bg-primary hover:bg-primary-600 text-white px-8 py-3"
              haptic="light"
            >
              {ITEM_REQUEST_CAPTURE_COPY.submitCta}
            </Button>
          ) : (
            <>
              <Button
                type="submit"
                disabled={primaryDisabled}
                className="bg-primary hover:bg-primary-600 text-white px-8 py-3"
                haptic="light"
              >
                {ITEM_REQUEST_CAPTURE_COPY.continueCta} →
              </Button>
              <p className="text-xs text-text-light max-w-xs text-right">
                {ITEM_REQUEST_CAPTURE_COPY.notSavedUntilSubmit}
              </p>
            </>
          )}
        </div>
      </div>
    </form>
  );
}
